import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, Check, AlertTriangle, FileText, Eye, Edit, Trash2, Ban } from 'lucide-react';

const DataConsentModal = ({ isOpen, onClose, onAccept, userData = {} }) => {
  const [consentChecks, setConsentChecks] = useState({
    dataProcessing: false,
    termsAccepted: false,
    privacyAccepted: false,
    marketingOptional: false
  });

  const [showARCOInfo, setShowARCOInfo] = useState(false);

  const allRequiredChecked = consentChecks.dataProcessing && 
                              consentChecks.termsAccepted && 
                              consentChecks.privacyAccepted;

  const handleAccept = () => {
    if (allRequiredChecked) {
      onAccept({
        ...consentChecks,
        timestamp: new Date().toISOString(),
        version: '1.0'
      });
    }
  };

  const toggleCheck = (key) => {
    setConsentChecks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          data-testid="data-consent-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Consentimiento de Datos</h2>
                <p className="text-sm text-slate-400">Ley 19.628 - Protección de Datos Personales</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="consent-close"
              className="text-slate-400 hover:text-white p-2"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Info Box */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-blue-200 text-sm">
                En cumplimiento con la <strong>Ley N° 19.628 sobre Protección de la Vida Privada</strong>, 
                requerimos su consentimiento expreso para el tratamiento de sus datos personales. 
                Puede revocar este consentimiento en cualquier momento.
              </p>
            </div>

            {/* Datos que Recopilamos */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="font-semibold text-white mb-3">Datos que Recopilamos</h3>
              <ul className="text-slate-300 text-sm space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-400">•</span>
                  <span><strong>Identificación:</strong> Nombre, email, RUT (opcional)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-400">•</span>
                  <span><strong>Contractuales:</strong> Contratos creados, firmados y gestionados</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-400">•</span>
                  <span><strong>Técnicos:</strong> IP, navegador, dispositivo (para seguridad)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-400">•</span>
                  <span><strong>Biométricos:</strong> Firma digital (solo con consentimiento adicional)</span>
                </li>
              </ul>
            </div>

            {/* Derechos ARCO */}
            <div className="bg-slate-800/50 rounded-xl p-4">
              <button
                onClick={() => setShowARCOInfo(!showARCOInfo)}
                data-testid="consent-arco-toggle"
                className="w-full flex items-center justify-between"
              >
                <h3 className="font-semibold text-white">Sus Derechos ARCO</h3>
                <span className="text-slate-400 text-sm">{showARCOInfo ? 'Ocultar' : 'Ver más'}</span>
              </button>
              
              <AnimatePresence>
                {showARCOInfo && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Eye className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-blue-400 text-sm">Acceso</span>
                        </div>
                        <p className="text-slate-300 text-xs">
                          Solicitar qué datos suyos tenemos almacenados
                        </p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Edit className="w-4 h-4 text-green-400" />
                          <span className="font-medium text-green-400 text-sm">Rectificación</span>
                        </div>
                        <p className="text-slate-300 text-xs">
                          Corregir datos inexactos o incompletos
                        </p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Trash2 className="w-4 h-4 text-orange-400" />
                          <span className="font-medium text-orange-400 text-sm">Cancelación</span>
                        </div>
                        <p className="text-slate-300 text-xs">
                          Eliminar sus datos cuando no sean necesarios
                        </p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Ban className="w-4 h-4 text-red-400" />
                          <span className="font-medium text-red-400 text-sm">Oposición</span>
                        </div>
                        <p className="text-slate-300 text-xs">
                          Oponerse al tratamiento en ciertos casos
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs mt-3">
                      Para ejercer estos derechos, envíe un correo a <strong>privacidad@notaryos.cl</strong>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-3">
              {/* Required: Data Processing */}
              <label 
                className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  consentChecks.dataProcessing ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/50 hover:bg-slate-800'
                }`}
                data-testid="consent-data-processing"
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={consentChecks.dataProcessing}
                    data-testid="consent-data-processing-checkbox"
                    onChange={() => toggleCheck('dataProcessing')}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consentChecks.dataProcessing 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-slate-500'
                  }`}>
                    {consentChecks.dataProcessing && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    Consiento el tratamiento de mis datos personales *
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Autorizo a NotaryOS a recopilar y procesar mis datos para proveer los servicios de la plataforma.
                  </p>
                </div>
              </label>

              {/* Required: Terms */}
              <label 
                className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  consentChecks.termsAccepted ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/50 hover:bg-slate-800'
                }`}
                data-testid="consent-terms"
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={consentChecks.termsAccepted}
                    data-testid="consent-terms-checkbox"
                    onChange={() => toggleCheck('termsAccepted')}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consentChecks.termsAccepted 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-slate-500'
                  }`}>
                    {consentChecks.termsAccepted && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    Acepto los Términos y Condiciones *
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    He leído y acepto los{' '}
                    <a data-testid="consent-terms-link" href="/legal/terminos" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      Términos y Condiciones de Uso
                    </a>
                  </p>
                </div>
              </label>

              {/* Required: Privacy */}
              <label 
                className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  consentChecks.privacyAccepted ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/50 hover:bg-slate-800'
                }`}
                data-testid="consent-privacy"
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={consentChecks.privacyAccepted}
                    data-testid="consent-privacy-checkbox"
                    onChange={() => toggleCheck('privacyAccepted')}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consentChecks.privacyAccepted 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-slate-500'
                  }`}>
                    {consentChecks.privacyAccepted && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    Acepto la Política de Privacidad *
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    He leído y acepto la{' '}
                    <a data-testid="consent-privacy-link" href="/legal/privacidad" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      Política de Privacidad
                    </a>
                  </p>
                </div>
              </label>

              {/* Optional: Marketing */}
              <label 
                className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  consentChecks.marketingOptional ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50 hover:bg-slate-800'
                }`}
                data-testid="consent-marketing"
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={consentChecks.marketingOptional}
                    data-testid="consent-marketing-checkbox"
                    onChange={() => toggleCheck('marketingOptional')}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consentChecks.marketingOptional 
                      ? 'bg-emerald-500 border-emerald-500' 
                      : 'border-slate-500'
                  }`}>
                    {consentChecks.marketingOptional && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    Deseo recibir comunicaciones (opcional)
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Acepto recibir novedades, actualizaciones legales y promociones por email.
                  </p>
                </div>
              </label>
            </div>

            {/* Warning if not all required */}
            {!allRequiredChecked && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs">
                  Debe aceptar todos los campos obligatorios (*) para continuar.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 p-6 flex space-x-3">
            <button
              onClick={onClose}
              data-testid="consent-cancel"
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-3 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleAccept}
              disabled={!allRequiredChecked}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                allRequiredChecked
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="consent-submit"
            >
              <Check className="w-4 h-4" />
              <span>Aceptar y Continuar</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DataConsentModal;
